import React, { useState, useEffect } from 'react';
import { AppUser } from '../../../types';
import { XMarkIcon, SpinnerIcon, UserCircleIcon } from '../../Icons';

interface AdvisorModalProps {
  isOpen: boolean;
  onClose: () => void;
  appUser: AppUser;
}

export const AdvisorModal: React.FC<AdvisorModalProps> = ({ isOpen, onClose, appUser }) => {
  const [fetchingAdvisor, setFetchingAdvisor] = useState(false);
  const [advisorName, setAdvisorName] = useState<string | null>(null);
  const [advisorPhone, setAdvisorPhone] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const fetchAdvisor = async () => {
      setFetchingAdvisor(true);
      try {
        const url = appUser.id_zoho
          ? `/api/zoho/broker?id=${encodeURIComponent(appUser.id_zoho)}`
          : appUser.email ? `/api/zoho/broker?email=${encodeURIComponent(appUser.email)}` : '';

        if (url) {
          const res = await fetch(url);
          if (res.ok) {
            const json = await res.json();
            const broker = Array.isArray(json.data) ? json.data[0] : json.data;
            const owner = broker?.Owner;
            if (owner?.name) {
              setAdvisorName(owner.name);
              setAdvisorPhone(owner.mobile || owner.phone || owner.phone_number || null);
            } else { setAdvisorName('Sin asesor asignado'); setAdvisorPhone(null); }
          } else { setAdvisorName('No se pudo consultar'); setAdvisorPhone(null); }
        } else { setAdvisorName('Sin datos de contacto'); setAdvisorPhone(null); }
      } catch (err) {
        setAdvisorName('Error al consultar');
        setAdvisorPhone(null);
      } finally { setFetchingAdvisor(false); }
    };
    fetchAdvisor();
  }, [isOpen, appUser.id_zoho, appUser.email]);

  if (!isOpen) return null;

  const digits = advisorPhone ? advisorPhone.replace(/\D/g, '') : '';
  const clean10 = digits.length >= 10 ? digits.slice(-10) : digits;
  const formattedPhone = clean10.length === 10
    ? `+57 ${clean10.slice(0, 3)} ${clean10.slice(3, 6)} ${clean10.slice(6)}`
    : (advisorPhone || '');

  const userName = appUser.nombreCompleto || appUser.displayName || 'Broker';
  const userZohoId = appUser.id_zoho || appUser.numeroIdentificacion || 'no registrado';
  const waMessage = `Hola, soy ${userName} y desde la aplicación broker se me informa que tú eres mi asesor comercial, mi id es ${userZohoId}`;
  const waUrl = clean10 ? `https://wa.me/57${clean10}?text=${encodeURIComponent(waMessage)}` : '#';

  return (
    <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[200] flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-white rounded-2xl sm:rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 my-auto animate-fade-in-up">
        <header className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div className="flex items-center gap-2.5">
            <UserCircleIcon className="w-6 h-6 text-sky-600" />
            <div>
              <h2 className="text-base font-black text-gray-800 uppercase tracking-tight">Conoce a tu Asesor</h2>
              <p className="text-[11px] text-sky-600 font-bold">Asesor Comercial Ciencuadras</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-full transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </header>

        <div className="p-6 space-y-4">
          {fetchingAdvisor ? (
            <div className="py-8 text-center text-sky-600 font-bold text-sm flex items-center justify-center gap-2">
              <SpinnerIcon className="w-5 h-5 animate-spin" /> Consultando asesor asignado...
            </div>
          ) : (
            <div className="space-y-3">
              {/* Row 1: Nombre */}
              <div className="flex items-center gap-3 p-3.5 bg-sky-50/60 border border-sky-100 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-sky-600 text-white font-extrabold flex items-center justify-center text-sm shadow-sm flex-shrink-0">
                  {advisorName ? advisorName.charAt(0).toUpperCase() : 'A'}
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-sky-600 tracking-wider block">Nombre del Asesor</span>
                  <span className="text-sm font-extrabold text-gray-900">{advisorName || 'Sin asignar'}</span>
                </div>
              </div>

              {advisorPhone && clean10 ? (
                <>
                  <p className="text-[11px] font-medium text-gray-500 px-1">Canales de contacto directo:</p>

                  {/* Row 2: Llamada */}
                  <a href={`tel:+57${clean10}`} className="flex items-center justify-between p-3.5 bg-white border border-gray-200 hover:border-sky-500 hover:bg-sky-50/40 rounded-xl transition-all group shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">📞</span>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 block uppercase">Llamada Telefónica</span>
                        <span className="text-xs font-black text-gray-800 font-mono group-hover:text-sky-700">{formattedPhone}</span>
                      </div>
                    </div>
                    <span className="bg-sky-100 text-sky-700 text-xs font-black px-3 py-1.5 rounded-lg group-hover:bg-sky-600 group-hover:text-white transition-colors">Llamar</span>
                  </a>

                  {/* Row 3: WhatsApp */}
                  <a href={waUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3.5 bg-emerald-50/80 border border-emerald-200 hover:border-emerald-500 hover:bg-emerald-100/60 rounded-xl transition-all group shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">💬</span>
                      <div>
                        <span className="text-[10px] font-bold text-emerald-600 block uppercase">WhatsApp Directo</span>
                        <span className="text-xs font-bold text-emerald-900">Enviar mensaje identificado</span>
                      </div>
                    </div>
                    <span className="bg-emerald-600 text-white text-xs font-black px-3 py-1.5 rounded-lg shadow-sm group-hover:bg-emerald-700 transition-colors">WhatsApp</span>
                  </a>
                </>
              ) : (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-center text-xs text-gray-500 font-medium">
                  No hay número telefónico registrado para el asesor.
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all">Cerrar</button>
        </footer>
      </div>
    </div>
  );
};
